import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './studentdashboard.css';
import { API_BASE, authFetch } from '../services/api.js';

function StudentDashboard() {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const parsedData = JSON.parse(userData);
        setStudent(parsedData.student);
      } catch (err) {
        setError('Failed to load user data');
        console.error('Error parsing user data:', err);
      }
    } else {
      navigate('/login');
    }
    setLoading(false);
  }, [navigate]);

  // Helper function to check if student has access to a feature for ANY subject
  const hasFeatureAccess = (featureName) => {
    if (!student?.accessible_subjects) return false;
    
    return student.accessible_subjects.some(subject => 
      subject.features && subject.features.includes(featureName)
    );
  };

  const getSubjectsWithFeature = (featureName) => {
    if (!student?.accessible_subjects) return [];
    
    return student.accessible_subjects.filter(subject => 
      subject.features && subject.features.includes(featureName)
    );
  };

  // Navigation handler with feature and subject context
  const handleFeatureNavigation = (route, featureName) => {
    const accessibleSubjects = getSubjectsWithFeature(featureName);
    
    if (accessibleSubjects.length === 0) {
      alert(`No access to ${featureName}. Contact your administrator.`);
      return;
    }

    // Store accessible subjects for the feature
    localStorage.setItem('currentFeatureAccess', JSON.stringify({
      feature: featureName,
      subjects: accessibleSubjects
    }));

    navigate(route);
  };

  const handleNavigation = (route) => {
    navigate(route);
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API_BASE}/auth/logout`, { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear localStorage (React's working copy)
      localStorage.removeItem('userData');
      localStorage.removeItem('student');
      localStorage.removeItem('token');
      localStorage.removeItem('currentFeatureAccess');
 
      // Notify Android to clear SharedPreferences (master copy)
      try {
        if (window.Android && typeof window.Android.onLogout === 'function') {
          window.Android.onLogout();
        }
      } catch (e) {
        console.warn('Android bridge onLogout not available:', e);
      }
 
      navigate('/login');
    }
  };

  if (loading) {
    return <div className="loading-container">Loading your dashboard...</div>;
  }

  if (error) {
    return <div className="error-container">{error}</div>;
  }

  if (!student) {
    navigate('/login');
    return null;
  }

  return (
    <div className="student-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="welcome-section">
          <h1>Welcome back, {student.name}! 🎓</h1>
          <p>Ready to continue your learning journey?</p>
        </div>
        <div className="user-actions">
          <span className="user-email">{student.email}</span>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </div>

      {/* Access Status */}
      {student.has_access ? (
        <div className="access-status success">
          <span>✅ You have access to {student.accessible_subjects?.length || 0} subjects</span>
        </div>
      ) : (
        <div className="access-status no-access">
          <span>⚠️ No subject access granted. Contact your administrator.</span>
        </div>
      )}

      {/* Subject Access Info */}
      {student.accessible_subjects && student.accessible_subjects.length > 0 && (
        <div className="subjects-section">
          <h3>Your Subjects & Features:</h3>
          <div className="subjects-grid">
            {student.accessible_subjects.map((subject) => (
              <div key={subject.subject_id} className="subject-card">
                <h4>{subject.subject_name}</h4>
                <p>Features available below ↓</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Navigation - Check unified access */}
      {student.has_access && (
        <div className="main-features">
          <h2>Learning Features</h2>
          <div className="features-grid">
            
            {/* Teaching Mode - Check unified access */}
            {hasFeatureAccess('teaching_mode') && (
              <div 
                className="feature-card" 
                onClick={() => handleFeatureNavigation('/teaching', 'teaching_mode')}
              >
                <div className="feature-icon">
                  <span>🎓</span>
                </div>
                <div className="feature-content">
                  <h3>Teaching Mode</h3>
                  <p>Learn step-by-step with micro chunks and interactive content</p>
                  <small className="access-info">
                    Available in: {getSubjectsWithFeature('teaching_mode').map(s => s.subject_name).join(', ')}
                  </small>
                </div>
                <div className="feature-arrow">→</div>
              </div>
            )}

            {/* Practice Q&A - Check teaching_mode access */}
            {hasFeatureAccess('teaching_mode') && (
              <div 
                className="feature-card" 
                onClick={() => handleNavigation('/practice-qa')}
              >
                <div className="feature-icon">
                  <span>📝</span>
                </div>
                <div className="feature-content">
                  <h3>Practice Q&A</h3>
                  <p>Practice with curated questions and detailed explanations</p>
                  <small className="access-info">
                    Available in: {getSubjectsWithFeature('teaching_mode').map(s => s.subject_name).join(', ')}
                  </small>
                </div>
                <div className="feature-arrow">→</div>
              </div>
            )}
            
            
            {/* Doubt Resolution - Check unified access */}
            {hasFeatureAccess('doubt_resolution') && (
              <div 
                className="feature-card" 
                onClick={() => handleFeatureNavigation('/doubt-solving', 'doubt_resolution')}
              >
                <div className="feature-icon">
                  <span>❓</span>
                </div>
                <div className="feature-content">
                  <h3>Doubt Resolution</h3>
                  <p>Get instant answers to your questions with AI assistance</p>
                  <small className="access-info">
                    Available in: {getSubjectsWithFeature('doubt_resolution').map(s => s.subject_name).join(', ')}
                  </small>
                </div>
                <div className="feature-arrow">→</div>
              </div>
            )}

            {/* Quiz - Check unified access */}
            {hasFeatureAccess('quiz') && (
              <div 
                className="feature-card" 
                onClick={() => handleFeatureNavigation('/quiz', 'quiz')}
              >
                <div className="feature-icon">
                  <span>📊</span>
                </div>
                <div className="feature-content">
                  <h3>Interactive Quiz</h3>
                  <p>Test your knowledge with adaptive quizzes</p>
                  <small className="access-info">
                    Available in: {getSubjectsWithFeature('quiz').map(s => s.subject_name).join(', ')}
                  </small>
                </div>
                <div className="feature-arrow">→</div>
              </div>
            )}

            {hasFeatureAccess('study_material') && (
              <div className="feature-card" onClick={() => navigate('/study-material')}>
                <div className="feature-icon"><span>📚</span></div>
                <div className="feature-content">
                  <h3>Study Material</h3>
                  <p>Access PDFs, notes, and video lectures for your subjects</p>
                </div>
                <div className="feature-arrow">→</div>
              </div>
            )}
            
            {hasFeatureAccess('teaching_mode') && (
              <div className="feature-card" onClick={() => handleFeatureNavigation('/new-learning', 'teaching_mode')}>
                <div className="feature-icon"><span>🆕</span></div>
                <div className="feature-content">
                  <h3>Teaching Mode (New)</h3>
                  <p>Try the new visual, block-based learning experience</p>
                </div>
                <div className="feature-arrow">→</div>
              </div>
            )}

            {hasFeatureAccess('live_quiz') && (
            <div className="feature-card" onClick={() => navigate('/livequiz')}>
              <div className="feature-icon"><span>🎯</span></div>
              <div className="feature-content">
                <h3>Live Quiz</h3>
                <p>Join a live quiz session hosted by your teacher</p>
                <small className="access-info">
                  Available in: {getSubjectsWithFeature('live_quiz').map(s => s.subject_name).join(', ')}
                </small>
              </div>
              <div className="feature-arrow">→</div>
            </div>
          )}
            
            
            
            
            
            {/* Enhanced Learning - Check unified access */}
            {hasFeatureAccess('enhanced_learning') && (
              <div 
                className="feature-card" 
                onClick={() => handleFeatureNavigation('/enhanced-chunks', 'enhanced_learning')}
              >
                <div className="feature-icon">
                  <span>⚡</span>
                </div>
                <div className="feature-content">
                  <h3>Enhanced Learning</h3>
                  <p>Advanced learning interface with smart recommendations</p>
                  <small className="access-info">
                    Available in: {getSubjectsWithFeature('enhanced_learning').map(s => s.subject_name).join(', ')}
                  </small>
                </div>
                <div className="feature-arrow">→</div>
              </div>
            )}

          </div>
          
          {/* Show message if no features are accessible */}
          {(!student.accessible_subjects || student.accessible_subjects.length === 0 || 
              !student.accessible_subjects.some(s => s.features && s.features.length > 0)) && (
              <div className="no-features-message">
                <p>⚠️ No learning features currently available. Contact your administrator for access.</p>
              </div>
            )}
        </div>
      )}

      {/* Footer Info */}
      <div className="dashboard-footer">
        <div className="app-info">
          <h4>Smart AI Tutor</h4>
          <p>Your personalized learning companion</p>
        </div>
        <div className="support-info">
          <p>Need help? Contact your administrator</p>
          <p>Language: {student.language_preference}</p>
        </div>
      </div>
    </div>
  );
}

export default StudentDashboard;